import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form } from 'formik';
import { object, string } from 'yup';

import {
  AlertStyled,
  DescriptionStyled,
  FormContainer,
  InputsContainer,
  PageHeader,
} from './styled';
import PageContainer from '../../../components/pageContainer';
import { SECTIONS } from '../../../constants/drawer';
import { LOAD_TITLE } from '../../../utils/functions';
import Loader from '../../../components/loader';
import AppForm, { AppTextField, FormButtons } from '../../../components/form';
import {
  useLazyGetFileCollectionSettingsQuery,
  useUpdateFileCollectionSettingsMutation,
  useLazyGetIndividualFileCollectionQuery,
} from '../../../redux/services/speciphicAsk';
import { PROMPT_CONFIG as API_ENDPOINT } from '../../../constants/filesCollectionSettings';
import { getChangedValues } from '../../../components/form/functions';
import UnauthorizedMessage from '../../../components/unauthorized';
import { DATABASE_SOURCE } from '../../../constants/dataSources';

const translationJSONPrefix = 'filesCollectionSettingsPage';

const unauthorizedTranslationPrefix = `${translationJSONPrefix}.unauthorizedMessages`;

const FILES_COLLECTION = `${translationJSONPrefix}.filesCollection`;
const PAGE_TITLE_SUFFIX = `${translationJSONPrefix}.pageTitleSuffix`;

const STORAGE_SOURCE_FIELDS = [
  'promptSystem',
  'promptUser',
  'promptRephrasedQuery',
  'onSuccessPrompt',
  'onFailurePrompt',
];

const DATABASE_SOURCE_FIELDS = [
  'pseudoCodePrompt',
  'dbQueryPrompt',
  'dbResponsePrompt',
];

const PAGE_TITLE = (t) =>
  `${t(`${translationJSONPrefix}.promptConfig.drawerMenuTitle`)}  | ${t(
    FILES_COLLECTION,
  )} | ${t(PAGE_TITLE_SUFFIX)}`;

const defaultStorageSourceDataState = {
  promptSystem: '',
  promptUser: '',
  promptRephrasedQuery: '',
  onSuccessPrompt: '',
  onFailurePrompt: '',
};

const defaultDatabaseSourceDataState = {
  pseudoCodePrompt: '',
  dbQueryPrompt: '',
  dbResponsePrompt: '',
};

const formSchema = (dataSourceType, t) => {
  const translationPrefix = (label) =>
    `${translationJSONPrefix}.promptConfig.form.${label}.errorMessages`;

  if (dataSourceType === DATABASE_SOURCE)
    return object({
      pseudoCodePrompt: string(),
      dbQueryPrompt: string(),
      dbResponsePrompt: string(),
    });

  return object({
    promptSystem: string(),
    promptUser: string(),
    promptRephrasedQuery: string(),
    onSuccessPrompt: string(),
    onFailurePrompt: string(),
  });
};

const formRenderFunction = (
  dataSourceType,
  { values, errors, touched, dirty, isSubmitting, resetForm, setFieldValue },
  formSending,
) => {
  const { t } = useTranslation();

  const translationPrefix = `${translationJSONPrefix}.promptConfig.form`;

  const fields =
    dataSourceType === DATABASE_SOURCE
      ? DATABASE_SOURCE_FIELDS
      : STORAGE_SOURCE_FIELDS;

  return (
    <Form>
      {fields.map((fieldName, i) => (
        <InputsContainer key={i}>
          <AppTextField
            style={{ width: '100%' }}
            name={fieldName}
            label={t(`${translationPrefix}.${fieldName}.label`)}
            placeholder={t(`${translationPrefix}.${fieldName}.placeholder`)}
            values={values}
            setFieldValue={setFieldValue}
            errors={errors}
            touched={touched}
            multiline
            minRows={5}
          />
        </InputsContainer>
      ))}

      <FormButtons
        resetForm={resetForm}
        dirty={dirty}
        isSubmitting={isSubmitting || formSending}
      />
    </Form>
  );
};

const PromptConfigPage = () => {
  const params = useParams();
  const { t } = useTranslation();

  useEffect(() => LOAD_TITLE(document, PAGE_TITLE(t)), []);

  const [collectionId, setCollectionId] = useState();
  const [initialFormData, setInitialFormData] = useState(
    defaultStorageSourceDataState,
  );
  const [formData, setFormData] = useState(defaultStorageSourceDataState);
  const [showAlert, setShowAlert] = useState({
    formUpdateError: false,
    formUpdateSuccess: false,
  });
  const [isDrawerOpen, setIsDrawerOpen] = useState(true);

  const [
    getFormData,
    {
      data: formDataDB,
      error: formError,
      isError: isGetFormError,
      isLoading: isGetFormLoading,
      isSuccess: isGetFormSuccess,
    },
  ] = useLazyGetFileCollectionSettingsQuery();

  const [
    getFilesCollectionInfo,
    {
      data: filesCollectionInfo,
      error: filesCollectionError,
      isError: isGetFCError,
      isLoading: isGetFCLoading,
      isSuccess: isGetFCSuccess,
    },
  ] = useLazyGetIndividualFileCollectionQuery();

  useEffect(() => {
    if (params?.collectionId) {
      setCollectionId(params?.collectionId);
      getFilesCollectionInfo({ collectionId: params?.collectionId });
      getFormData({
        collectionId: params.collectionId,
        endpoint: API_ENDPOINT,
      });
    }
  }, [params?.collectionId]);

  const [
    updateSettings,
    {
      data: formDataUpdateDB,
      isError: isUpdateFormError,
      isLoading: isUpdateFormLoading,
      isSuccess: isUpdateFormSuccess,
    },
  ] = useUpdateFileCollectionSettingsMutation();

  const closeAlert = () => {
    const tempAlert = { ...showAlert };
    Object.keys(tempAlert).forEach((a) => (tempAlert[a] = false));
    setShowAlert(tempAlert);
  };

  useEffect(() => {
    const tempAlert = { ...showAlert };
    tempAlert.formUpdateError = isUpdateFormError;
    setShowAlert(tempAlert);
  }, [isUpdateFormError]);

  useEffect(() => {
    const tempAlert = { ...showAlert };
    tempAlert.formUpdateSuccess = isUpdateFormSuccess;
    setShowAlert(tempAlert);
  }, [isUpdateFormSuccess]);

  useEffect(() => {
    if (showAlert.formUpdateError || showAlert.formUpdateSuccess)
      setTimeout(closeAlert, 5000);
  }, [showAlert.formUpdateError, showAlert.formUpdateSuccess]);

  useEffect(() => {
    if (isGetFormSuccess) {
      let data;

      if (filesCollectionInfo?.sourceType === DATABASE_SOURCE) {
        const {
          pseudoCodePrompt = '',
          dbQueryPrompt = '',
          dbResponsePrompt = '',
        } = formDataDB;

        data = {
          pseudoCodePrompt,
          dbQueryPrompt,
          dbResponsePrompt,
        };
      } else {
        const {
          promptSystem = '',
          promptUser = '',
          promptRephrasedQuery = '',
          onFailurePrompt = '',
          onSuccessPrompt = '',
        } = formDataDB;

        data = {
          promptSystem,
          promptUser,
          promptRephrasedQuery,
          onFailurePrompt,
          onSuccessPrompt,
        };
      }

      setFormData(data);
      setInitialFormData(data);
    }
  }, [formDataDB]);

  if (formError?.status === 403 || filesCollectionError?.status === 403)
    return (
      <UnauthorizedMessage
        drawerOpen={isDrawerOpen}
        drawer={SECTIONS.FILES_COLLECTION_SETTINGS}
        description={t(`${unauthorizedTranslationPrefix}.list`)}
      />
    );

  return (
    <PageContainer
      isDrawerOpen={(res) => setIsDrawerOpen(res)}
      drawer={SECTIONS.FILES_COLLECTION_SETTINGS}
      collectionId={collectionId}
    >
      <PageHeader>
        {`${filesCollectionInfo?.name || '...'} / ${t(
          `${translationJSONPrefix}.promptConfig.pageHeader`,
        )}`}
      </PageHeader>

      <DescriptionStyled>{collectionId}</DescriptionStyled>

      {collectionId && !isGetFormLoading ? (
        <FormContainer>
          <AlertStyled
            onClose={closeAlert}
            severity={showAlert.formUpdateError ? 'error' : 'success'}
            style={{
              marginTop:
                showAlert.formUpdateError || showAlert.formUpdateSuccess
                  ? 10
                  : -60,
            }}
          >
            {showAlert.formUpdateError &&
              t(`${translationJSONPrefix}.promptConfig.alert.updateFormError`)}
            {showAlert.formUpdateSuccess &&
              t(
                `${translationJSONPrefix}.promptConfig.alert.updateFormSuccess`,
              )}
          </AlertStyled>

          <AppForm
            initialValues={formData}
            validationSchema={formSchema(filesCollectionInfo?.sourceType, t)}
            onSubmit={(values, { setSubmitting }) => {
              const valuesToSend = getChangedValues(values, initialFormData);

              updateSettings({
                collectionId,
                data: valuesToSend,
                endpoint: API_ENDPOINT,
              });

              setSubmitting(false);
            }}
            formRenderFunction={(formikState) =>
              formRenderFunction(
                filesCollectionInfo?.sourceType,
                formikState,
                isUpdateFormLoading,
              )
            }
          />
        </FormContainer>
      ) : (
        <Loader label={t(`${translationJSONPrefix}.loadingMessage`)} />
      )}
    </PageContainer>
  );
};

export default PromptConfigPage;
